"use client";

import { useState } from "react";
import { ArrowRight, Award, Shield, Waves } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheVisionaries() {
  const [filter, setFilter] = useState("all");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const instructors = [{
    name: "Elena Vance",
    role: "Creative Director",
    bio: "Elena leads our vision, blending high-concept strategy with raw visual storytelling to define the next generation of brand identities.",
    certification: "Strategy",
    certIcon: "award",
    years: "12 yrs",
    favoriteBreak: "Concept",
    specialty: "Strategy",
    category: "beginners",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/creative_director_elena.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/creative_director_elena.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/creative_director_elena.png-wvc-sizes"
  }, {
    name: "Marcus Thorne",
    role: "Lead Producer",
    bio: "Marcus orchestrates our full-scale production pipeline, ensuring every frame meets our rigorous standards for cinematic excellence.",
    certification: "Production",
    certIcon: "shield",
    years: "15 yrs",
    favoriteBreak: "Execution",
    specialty: "Video",
    category: "advanced",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/lead_producer_marcus.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/lead_producer_marcus.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/lead_producer_marcus.webp-wvc-sizes"
  }, {
    name: "Jada Rivers",
    role: "Social Strategist",
    bio: "Jada specializes in social-first content scaling, turning viral moments into long-term brand equity for our global partners.",
    certification: "Social",
    certIcon: "waves",
    years: "8 yrs",
    favoriteBreak: "Engagement",
    specialty: "Social",
    category: "kids",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_strategist_jada.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_strategist_jada.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/social_strategist_jada.webp-wvc-sizes"
  }];
  const filtered = filter === "all" ? instructors : instructors.filter(i => i.category === filter);
  const renderCertIcon = (icon: string) => {
    if (icon === "shield") return <Shield className="size-3.5" />;
    if (icon === "waves") return <Waves className="size-3.5" />;
    return <Award className="size-3.5" />;
  };
  return <section data-section-id="726" id="the-visionaries" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="container mx-auto px-6 lg:px-12">
        {}
        <div ref={headerRef} className={`flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-4">THE VISIONARIES</p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-foreground">
              Meet the Studio
            </h2>
            <p className="mt-5 text-base text-muted-foreground max-w-xl">
              The creative minds behind our end-to-end production campaigns.
            </p>
          </div>

          <ToggleGroup type="single" value={filter} onValueChange={value => {
          if (value) setFilter(value);
        }} variant="outline" className="border border-border bg-card rounded-lg p-1 shadow-md self-start lg:self-end">
            <ToggleGroupItem value="all" className="font-mono text-xs uppercase tracking-wider px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border-0">
              All
            </ToggleGroupItem>
            <ToggleGroupItem value="beginners" className="font-mono text-xs uppercase tracking-wider px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border-0">
              Strategy
            </ToggleGroupItem>
            <ToggleGroupItem value="advanced" className="font-mono text-xs uppercase tracking-wider px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border-0">
              Production
            </ToggleGroupItem>
            <ToggleGroupItem value="kids" className="font-mono text-xs uppercase tracking-wider px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border-0">
              Social
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {filtered.map((instructor, i) => <Card data-index={i} key={instructor.name} className="group bg-card border border-border shadow-md hover:shadow-lg transition-all duration-500 overflow-hidden rounded-lg">
              <div className="relative">
                <AspectRatio ratio={4 / 5}>
                  <img src={instructor.image} data-wvc-srcset={instructor.srcset} data-wvc-sizes={instructor.sizes} alt={instructor.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </AspectRatio>
                <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 shadow-md rounded-lg flex items-center gap-1.5">
                  {renderCertIcon(instructor.certIcon)}
                  {instructor.certification}
                </Badge>
              </div>

              <CardContent className="px-6 pb-6">
                <h3 className="font-default text-[28px] leading-tight text-foreground">
                  {instructor.name}
                </h3>
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary mt-1.5">
                  {instructor.role}
                </p>

                {}
                <div className="mt-5 flex items-stretch gap-3 text-center">
                  <div className="flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      Tenure
                    </p>
                    <p className="font-default text-base text-foreground mt-1">
                      {instructor.years}
                    </p>
                  </div>
                  <Separator orientation="vertical" className="bg-border h-auto" />
                  <div className="flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      Focus
                    </p>
                    <p className="font-default text-base text-foreground mt-1">
                      {instructor.favoriteBreak}
                    </p>
                  </div>
                  <Separator orientation="vertical" className="bg-border h-auto" />
                  <div className="flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      Expertise
                    </p>
                    <p className="font-default text-base text-foreground mt-1">
                      {instructor.specialty}
                    </p>
                  </div>
                </div>

                <p className="mt-5 text-sm text-muted-foreground leading-relaxed line-clamp-2">
                  {instructor.bio}
                </p>

                <Button asChild variant="link" className="mt-5 px-0 h-auto font-mono text-xs uppercase tracking-[0.18em] text-foreground hover:text-primary group/btn">
                  <Link to="/TheStudioPage">
                    View Profile
                    <ArrowRight className="size-3.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div ref={quoteRef} className={`mt-20 md:mt-28 border-t border-b border-border py-12 md:py-16 transition-all duration-700 ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <blockquote className="max-w-5xl mx-auto text-center">
            <p className="font-default italic text-3xl md:text-4xl lg:text-5xl leading-[1.2] text-foreground">
              &ldquo;Strategy is the blueprint, but production is the adrenaline. We build campaigns that don&rsquo;t just exist — they impact.&rdquo;
            </p>
            <footer className="mt-8 flex items-center justify-center gap-3">
              <span className="h-px w-12 bg-primary" />
              <cite className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground not-italic">
                Elena Vance — Creative Director
              </cite>
              <span className="h-px w-12 bg-primary" />
            </footer>
          </blockquote>
        </div>
      </div>
    </section>;
}